import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import {
  differenceBy,
  intersectionBy,
  isNil,
  omit,
  remove,
  uniqBy
} from 'lodash';
import { Button, useNotify, useRefresh } from 'react-admin';
import { AutoSizer, MultiGrid } from 'react-virtualized';
import UserApi from '../../../core/apis/UserApi';
import Confirm from '../../../core/components/forked/layout/Confirm';
import {
  ColumnTitle,
  RowTitle,
  TableCellWithCheckbox
} from './datagridComponents/TableCells';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import { USERTYPE } from '../EnumUserType';
import { Resizable } from 're-resizable';

const spaceRegex = /[^\w\s]/gi;

const useStyles = makeStyles({
  tableGrid: {
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    maxHeight: '65vh',
    height: '65vh',
    border: '1px solid #E0E0E0',
    width: '100%'
  },
  tableGridForWizard: {
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    maxHeight: '66vh',
    height: '66vh',
    border: '1px solid #E0E0E0',
    width: '100%'
  },
  leftGrid: {
    borderRight: '1px solid #E0E0E0'
  },
  topGrid: {
    borderBottom: '1px solid #E0E0E0',
    backgroundColor: '#f5f5f5',
    fontFamily: '"Segoe UI",Arial,sans-serif',
    fontWeight: 500,
    color: '#757575'
  },
  back_0: {
    background: '#f5f5f5'
  },
  back_1: {
    background: '#ffffff'
  },
  centerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  saveButtonContainer: {
    paddingTop: 10,
    paddingBottom: 10
  },
  closeButton: {
    border: '1px solid #E0E0E0',
    width: '67px',
    height: '30px',
    borderRadius: '2px',
    backgroundColor: '#FFFFFF',
    marginLeft: '10px',
    '&:disabled': {
      //getting same css rules as the disabled class for the saveButton to be consistent
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
      color: 'rgba(0, 0, 0, 0.26)',
      boxShadow: 'none'
    }
  },
  backButton: {
    border: '1px solid #E0E0E0',
    width: '67px',
    height: '30px',
    borderRadius: '2px',
    backgroundColor: '#FFFFFF',
    marginLeft: '10px'
  },
  progress: {
    position: 'absolute',
    top: '55%',
    left: '55%'
  }
});

const isDisabled = (groupId, connectedUserIds, authorization, superAdmin) => {
  if (superAdmin) {
    return false;
  }
  // Handle disable for one fund
  if (!authorization.children) {
    const id = `${authorization.investorId}:${authorization.fundId}:${groupId}`;
    const idInvestorLevel = `*:${authorization.fundId}:${groupId}`;
    return !(
      connectedUserIds.includes(id) ||
      connectedUserIds.includes(idInvestorLevel)
    );
  } else {
    // Handle disable for all funds
    if (authorization.id === '*') {
      return !authorization.children.every(node => {
        if (node.children) {
          const authorizationsIdsForGroup = node.children.map(item => {
            return {
              id: `${item.investorId}:${item.fundId}:${groupId}`,
              idInvestorLevel: `*:${item.fundId}:${groupId}`
            };
          });
          return authorizationsIdsForGroup.every(
            item =>
              connectedUserIds.includes(item.id) ||
              connectedUserIds.includes(item.idInvestorLevel)
          );
        } else {
          // If there is no children
          const id = `${node.investorId}:${node.fundId}:${groupId}`;
          const idInvestorLevel = `*:${node.fundId}:${groupId}`;

          return (
            connectedUserIds.includes(id) ||
            connectedUserIds.includes(idInvestorLevel)
          );
        }
      });
    } else {
      // Handle disable for group of funds
      const authorizationsIdsForGroup = authorization.children.map(item => {
        return {
          id: `${item.investorId}:${item.fundId}:${groupId}`,
          idInvestorLevel: `*:${item.fundId}:${groupId}`
        };
      });
      return !authorizationsIdsForGroup.every(item => {
        return (
          connectedUserIds.includes(item.id) ||
          connectedUserIds.includes(item.idInvestorLevel)
        );
      });
    }
  }
};

const createChild = (
  node,
  leafs,
  optionValue,
  optionText,
  parent,
  indentation
) => {
  const leaf = {
    id: `${parent.id}:${node[optionValue]}`,
    displayId: node[optionValue],
    authId: `${node.investorId}:${node.fundId}`,
    investorId: node.investorId,
    fundId: node.fundId,
    label: node[optionText] || '',
    tooltipLabel: `${node[optionValue]} - ${node[optionText]}`,
    parent: parent,
    indentation: indentation
  };
  leafs[leaf.authId] = leaf;
  return leaf;
};

const fieldSort = (a, b) =>
  a.label
    .replace(spaceRegex, '')
    .localeCompare(b.label.replace(spaceRegex, ''));

const createAuthTree = (
  authorizations,
  optionText,
  optionValue,
  groupBy,
  groupByLabel
) => {
  const leafs = {};
  const groupsDict = {};
  let orphans = [];

  // Adds the option "All" as parent
  const root = {
    id: '*',
    label: 'All',
    indentation: 0
  };

  let tree = authorizations.reduce((list, authorization) => {
    if (authorization !== undefined) {
      const groupId = authorization[groupBy];
      if (groupId) {
        let group = groupsDict[groupId];
        if (!group) {
          const label = authorization[groupByLabel] || '' + groupId;
          group = {
            id: `${root.id}:${groupId}`,
            displayId: groupId,
            label: label || '',
            tooltipLabel: `${groupId} - ${label}`,
            parent: root,
            indentation: 1
          };
          group.children = [
            createChild(authorization, leafs, optionValue, optionText, group, 2)
          ];

          groupsDict[groupId] = group;
          list.push(group);
        } else {
          const child = group.children.find(
            item => item.fundId === authorization.fundId
          );
          if (!child) {
            group.children.push(
              createChild(
                authorization,
                leafs,
                optionValue,
                optionText,
                group,
                2
              )
            );
          }
        }
      } else {
        orphans.push(
          createChild(authorization, leafs, optionValue, optionText, root, 1)
        );
      }
    }
    return list;
  }, []);

  // Sort groups
  tree.sort(fieldSort);

  // If there are orphans, display them after fund families
  if (orphans.length > 0) {
    orphans = uniqBy(orphans, item => item.id);
    // Sort orphans funds
    orphans.sort(fieldSort);

    // Concat orphans and groups, and put the orphans in last positions
    tree = tree.concat(orphans);
  }

  root.children = tree;

  return [root, leafs];
};

const getTreeRoot = node => {
  if (node.parent) {
    return getTreeRoot(node.parent);
  }
  return node;
};

const getRows = (node, expandedNodeIds) => {
  const rows = [node];
  expandedNodeIds[node.id] &&
    node.children &&
    node.children.forEach(child => {
      rows.push(...getRows(child, expandedNodeIds));
    });
  return rows;
};

const updateNodeAndChildren = (node, groupId, checked, ids) => {
  ids[`${node.id}:${groupId}`] = checked ? 1 : 0;
  node.children &&
    node.children.forEach(
      child => (ids = updateNodeAndChildren(child, groupId, checked, ids))
    );
  return ids;
};

const updateParents = (node, groupId, ids) => {
  if (node.parent) {
    let val = 0;
    if (
      node.parent.children.some(child => ids[`${child.id}:${groupId}`] === 2)
    ) {
      val = 2;
    } else {
      const selCount = node.parent.children.filter(
        child => ids[`${child.id}:${groupId}`] === 1
      ).length;
      val =
        selCount === 0 ? 0 : selCount === node.parent.children.length ? 1 : 2;
    }
    ids[`${node.parent.id}:${groupId}`] = val;
    ids = updateParents(node.parent, groupId, ids);
  }
  return ids;
};

const updateIds = (ids, node, groupId, checked) => {
  if (node) {
    ids = updateNodeAndChildren(node, groupId, checked, ids);
    ids = updateParents(node, groupId, ids);
  }
  return ids;
};

const createSelectedGroups = ids => {
  const groups = {};
  ids.forEach(id => {
    const [, , , groupId] = id.split(':');
    groups[groupId] = true;
  });
  return groups;
};

const UserAuthorizationDatagrid = props => {
  const multiGridRef = useRef();

  const [columnWidths, setColumnWidths] = useState({});

  const getColumnWidth = useCallback(
    ({ index }) => {
      if (columnWidths[index]) {
        return columnWidths[index];
      } else {
        return index === 0 ? 250 : 100;
      }
    },
    [columnWidths]
  );

  const onResize = useCallback(
    (index, newSize) => {
      // Parse newSize to an integer to remove 'px'
      let newSizeInt = parseInt(newSize, 10);
      setColumnWidths({
        ...columnWidths,
        [index]: newSizeInt
      });
      multiGridRef?.current?.recomputeGridSize();
    },
    [columnWidths]
  );

  const {
    groupByLabel,
    optionText,
    groupBy,
    // userType defines the user we are creating/editing
    userType,
    authorizations: originalAuthorizations,
    groups,
    groupsFilter,
    optionValue,
    id,
    onChangeAuthorizations,
    hasWriteAuthorizationPermission,
    authorizationsConnectedUser,
    // userProfile defines the user who is connected
    userProfile,
    wizardMode,
    refreshList,
    creation,
    createdContactId,
    authorizationsGridToDeleteRef,
    permissionGroupsUploaded,
    setCurrentAuthorizations = () => null,
    currentAuthorizations = [],
    columnLabel = '',
    setPermissionsStaging = () => null,
    permissionsStaging = []
  } = props;
  // when perf debugging, check what's changed in the properties
  //useWhyDidYouUpdate('UserAuthorizationDatagrid', props);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [chkEvts, setChckEvts] = useState([]);
  const [selectedCkIds, setSelectedCkIds] = useState({});
  const expandedNodeIds = useRef({ '*': true });
  const authorizationsToUpdate = useRef([]);
  const refresh = useRefresh();
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const classes = useStyles();

  const superAdmin = userProfile.userType === USERTYPE.superAdmin;

  const globalDisabled = !superAdmin && !hasWriteAuthorizationPermission;

  const connectedUserIds = useMemo(() => {
    if (!superAdmin) {
      // investorId:fundId:groupId
      return authorizationsConnectedUser.map(item => item.id);
    }
    return [];
  }, [authorizationsConnectedUser, superAdmin]);

  const [authTree, leafsById] = useMemo(() => {
    const treeAndLeaf = createAuthTree(
      originalAuthorizations,
      optionText,
      optionValue,
      groupBy,
      groupByLabel
    );
    setRows(getRows(treeAndLeaf[0], expandedNodeIds.current));
    return treeAndLeaf;
  }, [originalAuthorizations, optionText, optionValue, groupBy, groupByLabel]);

  // Filter groups
  const groupsToShow = useMemo(() => {
    if (groupsFilter === 'ONLY_ASSIGNED') {
      const selGroups = createSelectedGroups(props.ids);
      return groups.filter(group => !!selGroups[group.id]);
    } else {
      return groups;
    }
  }, [groups, groupsFilter, props.ids]);

  const updateCks = useCallback(
    (ids, checked, forcedCkIds) => {
      setSelectedCkIds(selCkIds => {
        let ckIds = forcedCkIds ? { ...forcedCkIds } : { ...selCkIds };
        ids.forEach(rowId => {
          const [, investorId, fundId, groupId] = rowId.split(':'); // first part is userId
          ckIds = updateIds(
            ckIds,
            leafsById[`${investorId}:${fundId}`],
            groupId,
            checked
          );
        });
        return ckIds;
      });
    },
    [leafsById]
  );

  useEffect(() => updateCks(props.ids, true), [props.ids, updateCks]);

  const formReset = useCallback(() => {
    if (!creation) {
      if (authorizationsToUpdate.current.length !== 0) {
        authorizationsToUpdate.current = [];
        updateCks(props.ids, true, {});
        onChangeAuthorizations(false);
      }
    }
  }, [props.ids, onChangeAuthorizations, updateCks, creation]);

  const handleCheckboxChange = useCallback(
    (data, groupId, checked) => {
      // Note: checked === true when we UN-TICK a checkbox

      setChckEvts(evts => {
        return [...evts, { data, groupId, checked }];
      });

      if (creation) {
        const browseChildren = checkbox => {
          // We loop through the checkbox children until we reach a leaf.
          if (checkbox.children) {
            checkbox.children.forEach(child => browseChildren(child));
            return;
          }

          // We reach a leaf, get the index of the authorization in our state of authorization.
          const indexAuthorizationsGridRef = currentAuthorizations.findIndex(
            authorization =>
              authorization.id ===
              `${createdContactId}:${checkbox.investorId}:${checkbox.fundId}:${groupId}`
          );
          const indexPermissionsStaging = permissionsStaging.findIndex(
            authorization =>
              authorization.id ===
              `${createdContactId}:${checkbox.investorId}:${checkbox.fundId}:${groupId}`
          );

          const permission = {
            id: `${createdContactId}:${checkbox.investorId}:${checkbox.fundId}:${groupId}`,
            fundId: checkbox.fundId,
            investorId: checkbox.investorId,
            groupId,
            userId: createdContactId,
            checked: !checked
          };

          if (indexAuthorizationsGridRef === -1) {
            setCurrentAuthorizations(prev => [...prev, permission]);
          }

          if (indexPermissionsStaging === -1) {
            setPermissionsStaging(prev => [...prev, permission]);
          }

          function filterState(setState) {
            setState(prev => [
              ...prev.filter(
                authorization =>
                  authorization.id !==
                  `${createdContactId}:${checkbox.investorId}:${checkbox.fundId}:${groupId}`
              )
            ]);
          }

          if (checked && indexAuthorizationsGridRef > -1) {
            filterState(setCurrentAuthorizations);
          }

          if (checked && indexPermissionsStaging > -1) {
            filterState(setPermissionsStaging);
          }

          if (permissionGroupsUploaded.length !== 0) {
            const indexAuthorizationsGridToDeleteRef = authorizationsGridToDeleteRef.current.findIndex(
              authorization =>
                authorization.id ===
                `${createdContactId}:${checkbox.investorId}:${checkbox.fundId}:${groupId}`
            );
            const indexPermissionGroupsUploaded = permissionGroupsUploaded.findIndex(
              authorization =>
                authorization.id ===
                `${createdContactId}:${checkbox.investorId}:${checkbox.fundId}:${groupId}`
            );

            // Was this authorization already submitted?
            if (indexPermissionGroupsUploaded > -1) {
              // Is this authorization already in the 'to delete' list ?
              if (indexAuthorizationsGridToDeleteRef > -1) {
                authorizationsGridToDeleteRef.current.splice(
                  indexAuthorizationsGridToDeleteRef,
                  1
                );
              } else {
                authorizationsGridToDeleteRef.current.push({
                  userId: createdContactId,
                  fundId: checkbox.fundId,
                  investorId: checkbox.investorId,
                  groupId: groupId,
                  id: `${createdContactId}:${checkbox.investorId}:${checkbox.fundId}:${groupId}`
                });
              }
            }
          }
        };
        browseChildren(data);
      }
    },
    [
      creation,
      currentAuthorizations,
      permissionGroupsUploaded,
      permissionsStaging,
      createdContactId,
      setCurrentAuthorizations,
      setPermissionsStaging,
      authorizationsGridToDeleteRef
    ]
  );

  useEffect(() => {
    if (chkEvts.length === 0) {
      return;
    }
    if (chkEvts.length > 1) {
      console.error(
        'UserAuthorizationDatagrid.handleCheckboxChange: more than one evt : need to handle them all'
      );
    }
    const { data, groupId, checked } = chkEvts[0];

    // Refresh the checkboxes
    setSelectedCkIds(ids => updateIds({ ...ids }, data, groupId, !checked));

    const authorizations = [];

    if (!data.children) {
      authorizations.push({
        userId: id,
        groupId: groupId,
        investorId: data.investorId,
        fundId: data.fundId,
        id: `${data.investorId}:${data.fundId}:${groupId}`,
        checked: !checked
      });
    } else if (data.parent) {
      data.children.forEach(node =>
        authorizations.push({
          userId: id,
          groupId: groupId,
          investorId: node.investorId,
          fundId: node.fundId,
          id: `${node.investorId}:${node.fundId}:${groupId}`,
          checked: !checked
        })
      );
    } else {
      data.children.forEach(node => {
        if (node.children) {
          node.children.forEach(leaf =>
            authorizations.push({
              userId: id,
              groupId: groupId,
              investorId: leaf.investorId,
              fundId: leaf.fundId,
              id: `${leaf.investorId}:${leaf.fundId}:${groupId}`,
              checked: !checked
            })
          );
        } else {
          authorizations.push({
            userId: id,
            groupId: groupId,
            investorId: node.investorId,
            fundId: node.fundId,
            id: `${node.investorId}:${node.fundId}:${groupId}`,
            checked: !checked
          });
        }
      });
    }

    // Saving in the state the payload structure and add or remove the real changes
    let newAuthorizationsToUpdate = [...authorizationsToUpdate.current];
    let newAuthorizationsFromAllOption = null;

    // If user clicks on "All" option
    if (!data.parent) {
      // Getting the original authorizations for the group which selected by the user
      // And formats the object as the payload
      const originalAuthorizationsForThisGroup = originalAuthorizations
        .filter(item => item.groupId === groupId)
        .map(item => ({
          userId: id,
          groupId: groupId,
          investorId: item.investorId,
          fundId: item.fundId,
          id: `${item.investorId}:${item.fundId}:${groupId}`,
          checked: !checked
        }));
      // Removing the all authorizations for this group to be safe with the new authorizations
      remove(newAuthorizationsToUpdate, item => item.groupId === groupId);
      if (checked) {
        // If the "All" option is unchecked, gets all the items to remove
        newAuthorizationsFromAllOption = intersectionBy(
          originalAuthorizationsForThisGroup,
          authorizations,
          item => item.id
        );
      } else {
        // If the "All" option is checked, gets all the items to add
        newAuthorizationsFromAllOption = differenceBy(
          authorizations,
          originalAuthorizationsForThisGroup,
          item => item.id
        );
      }
    }

    (newAuthorizationsFromAllOption || authorizations).forEach(auth => {
      const authAlreadyToUpdate = authorizationsToUpdate.current.find(
        item => item.id === auth.id
      );
      if (!isNil(authAlreadyToUpdate)) {
        remove(newAuthorizationsToUpdate, item => item.id === auth.id);
      } else {
        newAuthorizationsToUpdate = newAuthorizationsToUpdate.concat(auth);
      }
    });

    setChckEvts([]);
    authorizationsToUpdate.current = newAuthorizationsToUpdate;
    if (!creation) {
      // Notify the parent to prevent navigation if there are changes
      onChangeAuthorizations(
        newAuthorizationsToUpdate.length > 0,
        newAuthorizationsToUpdate
      );
    }
  }, [
    id,
    onChangeAuthorizations,
    originalAuthorizations,
    authTree,
    chkEvts,
    creation
  ]);

  const handleClickSave = useCallback(async () => {
    setOpenConfirm(false);
    setLoading(true);
    const authorizationsToCreate = [];
    const authorizationsToDelete = [];
    authorizationsToUpdate.current.forEach(item =>
      item.checked
        ? authorizationsToCreate.push(item)
        : authorizationsToDelete.push(item)
    );
    let errors = false;

    // Request for deletion
    if (authorizationsToDelete.length > 0) {
      // Paylad without 'id' and 'checked' fields
      const payload = authorizationsToDelete.map(item =>
        omit(item, ['id', 'checked'])
      );
      await UserApi.deleteUserauthorizations(payload).catch(error => {
        notifyServerError(error);
        errors = true;
        // Revert ids value in error
        updateCks(
          authorizationsToDelete.map(auth => auth.id),
          true
        );
      });
    }

    // Request for creation
    if (authorizationsToCreate.length > 0) {
      // Paylad without 'id' and 'checked' fields
      const payload = authorizationsToCreate.map(item =>
        omit(item, ['id', 'checked'])
      );
      await UserApi.createUserauthorizations(payload).catch(error => {
        notifyServerError(error);
        errors = true;
        // Revert ids value in error
        updateCks(
          authorizationsToCreate.map(auth => auth.id),
          false
        );
      });
    }

    authorizationsToUpdate.current = [];

    if (!creation) {
      // Notify the parent to allow navigation
      onChangeAuthorizations(false);

      if (!errors) {
        notify('ra.notification.custom_updated_message');
        if (authorizationsToDelete.length > 0) {
          refresh();
        } else {
          refreshList();
        }
      }
    }

    setLoading(false);
  }, [
    refresh,
    onChangeAuthorizations,
    refreshList,
    notify,
    notifyServerError,
    updateCks,
    creation
  ]);

  // need this callback to workaround a refresh bug for MultiGrid (no render on maingrid when cellRenderer change)
  const onSectionRendered = useCallback(
    params => {
      selectedCkIds || groupsToShow || console.log(params);
    },
    [selectedCkIds, groupsToShow]
  );

  const cellRenderer = useCallback(
    ({
      columnIndex,
      _isScrolling,
      isVisible,
      key,
      _parent,
      rowIndex,
      style
    }) => {
      if (!isVisible) {
        return null;
      }
      let content = null;
      const clList = [];
      if (rowIndex === 0) {
        let text;
        const first = columnIndex === 0;
        if (first) {
          if (columnLabel !== '') {
            text = columnLabel;
          } else {
            text =
              userType === USERTYPE.regularContact ||
              userType === USERTYPE.investorAdmin
                ? 'Investors'
                : 'Funds';
          }
        } else {
          text = groupsToShow[columnIndex - 1].name;
        }
        content = (
          <Resizable
            enable={{
              top: false,
              right: true,
              bottom: false,
              left: false,
              topRight: false,
              bottomRight: false,
              bottomLeft: false,
              topLeft: false
            }}
            axis="x"
            onResize={(_e, _direction, ref, _d) => {
              onResize(columnIndex, ref.style.width);
            }}
          >
            <ColumnTitle center={!first} label={text} />
          </Resizable>
        );
      } else if (columnIndex === 0) {
        const rowData = rows[rowIndex - 1];
        const rowDataFinal = {
          ...rowData,
          expand: !!expandedNodeIds.current[rowData.id]
        };
        content = (
          <RowTitle
            data={rowDataFinal}
            textOverflow={true}
            onRowChange={() => {
              if (expandedNodeIds.current[rowDataFinal.id]) {
                delete expandedNodeIds.current[rowDataFinal.id];
              } else {
                expandedNodeIds.current[rowDataFinal.id] = true;
              }
              setRows(
                getRows(getTreeRoot(rowDataFinal), expandedNodeIds.current)
              );
            }}
          />
        );
        clList.push(classes['back_' + (rowIndex % 2)]);
      } else {
        clList.push(classes['back_' + (rowIndex % 2)], classes.centerContent);
        const rowData = rows[rowIndex - 1];
        const groupData = groupsToShow[columnIndex - 1];
        const checkId = `${rowData.id}:${groupData.id}`;
        const checked = selectedCkIds[checkId] === 1;
        content = (
          <TableCellWithCheckbox
            id={checkId}
            label={rowData.tooltipLabel}
            disabled={
              globalDisabled ||
              isDisabled(groupData.id, connectedUserIds, rowData, superAdmin)
            }
            checked={checked}
            indeterminate={selectedCkIds[checkId] === 2}
            handleCheckboxChange={() =>
              handleCheckboxChange(rowData, groupData.id, checked)
            }
          />
        );
      }
      return (
        <div
          key={key}
          style={{ ...style, width: getColumnWidth({ index: columnIndex }) }}
          className={clList.join(' ')}
        >
          {content}
        </div>
      );
    },
    [
      rows,
      selectedCkIds,
      groupsToShow,
      handleCheckboxChange,
      userType,
      classes,
      connectedUserIds,
      globalDisabled,
      superAdmin,
      columnLabel,
      getColumnWidth,
      onResize
    ]
  );
  return (
    <>
      {loading && (
        <div className={classes.progress}>
          <CircularProgress />
        </div>
      )}
      <Grid container>
        <Grid
          item
          classes={{
            item: !wizardMode ? classes.tableGrid : classes.tableGridForWizard
          }}
        >
          <AutoSizer>
            {({ height, width }) => (
              <MultiGrid
                // Add rows and connectedUserIds as props to force MultiGrid to rerender cellRenderer when rows have changed
                // https://github.com/bvaughn/react-virtualized/issues/1108#issuecomment-468484066
                rows={rows}
                ref={multiGridRef}
                connectedUserIds={connectedUserIds}
                onSectionRendered={onSectionRendered}
                cellRenderer={cellRenderer}
                columnWidth={getColumnWidth}
                columnCount={groupsToShow.length + 1}
                fixedColumnCount={1}
                fixedRowCount={1}
                height={height}
                rowHeight={30}
                rowCount={rows.length + 1}
                width={width}
                tabIndex={null}
                classNameBottomLeftGrid={classes.leftGrid}
                classNameTopLeftGrid={classes.leftGrid + ' ' + classes.topGrid}
                classNameTopRightGrid={classes.topGrid}
              />
            )}
          </AutoSizer>
        </Grid>
        {!wizardMode && (
          <>
            <Grid
              container
              classes={{ container: classes.saveButtonContainer }}
            >
              <Button
                onClick={() => setOpenConfirm(true)}
                label="Save"
                variant="contained"
                color="primary"
                disabled={
                  loading ||
                  !hasWriteAuthorizationPermission ||
                  authorizationsToUpdate.current.length === 0
                }
                id="save"
              >
                {loading ? <CircularProgress size={15} /> : <></>}
              </Button>
              <Button
                label="Cancel"
                className={classes.closeButton}
                disabled={
                  loading ||
                  !hasWriteAuthorizationPermission ||
                  authorizationsToUpdate.current.length === 0
                }
                onClick={formReset}
                id={'cancel'}
              />
              <Button
                label="Back"
                className={classes.backButton}
                onClick={props.history.goBack}
                id={'back'}
              />
            </Grid>

            <Confirm
              isOpen={openConfirm}
              title="Edit Access Rights"
              content="You are editing Access Rights for this contact, are you sure you want to proceed?"
              onConfirm={handleClickSave}
              onClose={() => setOpenConfirm(false)}
            />
          </>
        )}
      </Grid>
    </>
  );
};

UserAuthorizationDatagrid.defaultProps = {
  groupBy: 'investorId',
  optionText: 'fundName',
  optionValue: 'fundId',
  authorizations: []
};

export default UserAuthorizationDatagrid;
